import { BeforeLoadContextOptions, ParsedLocation, redirect } from '@tanstack/react-router';

import { AppRouterContext } from '@/app/lib/tanstack-router';
import { getTokens } from '@/shared/auth/user-store';
import { getCurrentUserQueryOptions } from '@/shared/user/queries';

type AuthGuardParams = {
  context: BeforeLoadContextOptions<any, any, any, AppRouterContext, AppRouterContext>['context'];
  location: ParsedLocation;
};

/**
 * Ensures that the user is authenticated before allowing access to a resource.
 * Checks for the presence of an access token and redirects to the login page
 * if the user is not authenticated.
 *
 * The current location is preserved for redirecting back after successful login.
 *
 * @return A promise that resolves if the user is authenticated or throws a
 * redirection error if not.
 */
export async function requireAuth({ context, location }: AuthGuardParams) {
  const isAuthenticated = !!getTokens()?.access_token;

  if (!isAuthenticated) {
    throw redirect({
      to: '/login',
      search: {
        // Preserve the current location so we can redirect back after login
        redirect: location.pathname + location.searchStr
      }
    });
  }

  await context.queryClient.ensureQueryData({
    ...getCurrentUserQueryOptions()
  });
}

/**
 * Ensures that the current user is not authenticated.
 * If the user is authenticated,
 * the function will redirect them to the root ('/') route.
 */
export function requireGuest() {
  const isAuthenticated = !!getTokens()?.access_token;

  if (isAuthenticated) {
    throw redirect({
      to: '/'
    });
  }
}
